import { createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';
import CalendarService from './Calendar.service';
import {
  CalendarEventCreatePayload,
  CalendarEventFormData,
  CalendarEventUpdatePayload,
  CalendarInterview,
  GoogleCalendarEventRaw,
} from './Calendar.type';
import { mapGoogleCalendarEventToFormData } from './Calendar.mapper';
import { errorHandlerThunk } from '../App/App.thunk';

const interviewsRequestInFlight = new Set<number>();

export const getPersonInterviewsThunk = createAsyncThunk(
  'calendar/getPersonInterviewsThunk',
  async (personId: number, { dispatch, rejectWithValue }) => {
    try {
      const response = await CalendarService.getInterviewsByPerson(personId);
      const payload = response.data;
      const interviews = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      return {
        personId,
        interviews: interviews as CalendarInterview[],
      };
    } catch (error: unknown) {
      dispatch(errorHandlerThunk(error));
      return rejectWithValue(error);
    } finally {
      interviewsRequestInFlight.delete(personId);
    }
  },
  {
    condition: personId => {
      if (interviewsRequestInFlight.has(personId)) return false;
      interviewsRequestInFlight.add(personId);
      return true;
    },
  },
);

export const refreshPersonInterviewsThunk =
  (personId: number) => (dispatch: ThunkDispatch<any, any, any>) => {
    interviewsRequestInFlight.delete(personId);
    return dispatch(getPersonInterviewsThunk(personId));
  };

export const createCalendarEventThunk =
  (payload: CalendarEventCreatePayload) =>
  (dispatch: ThunkDispatch<any, any, any>) =>
    CalendarService.createEvent(payload)
      .then(response => response.data)
      .catch((error: unknown) => {
        dispatch(errorHandlerThunk(error));
        return false as const;
      });

export const getCalendarEventThunk =
  (eventId: string) => (dispatch: ThunkDispatch<any, any, any>) =>
    CalendarService.getEvent(eventId)
      .then(response => {
        const raw = (response.data?.data ??
          response.data) as GoogleCalendarEventRaw;
        return mapGoogleCalendarEventToFormData(raw) as CalendarEventFormData;
      })
      .catch((error: unknown) => {
        dispatch(errorHandlerThunk(error));
        return false as const;
      });

export const updateCalendarEventThunk =
  (eventId: string, payload: CalendarEventUpdatePayload) =>
  (dispatch: ThunkDispatch<any, any, any>) =>
    CalendarService.updateEvent(eventId, payload)
      .then(response => response.data)
      .catch((error: unknown) => {
        dispatch(errorHandlerThunk(error));
        return false as const;
      });

export const deleteCalendarEventThunk =
  (interviewId: number) => (dispatch: ThunkDispatch<any, any, any>) =>
    CalendarService.deleteEvent(interviewId)
      .then(response => response.data)
      .catch((error: unknown) => {
        dispatch(errorHandlerThunk(error));
        return false as const;
      });
